'use client';

import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useI18n } from 'next-localization';

import { intersection } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import { AutocompleteQueries } from '@/features/landing/landing-search.types';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { LISTING_PAGE } from '@/lib/constants';
import { useOasApp } from '@/providers';
import { Pages, RouteSegments } from '@/types/Routing';
import { NeuButton } from '@hcahealthcaredpx/neutron-react';

import { ALL_FACILITY_OPTION, DEFAULT_REQ_PAYLOAD, DISTANCE_SORT_BY } from '../../constants';
import { useListingContext } from '../../hooks';
import { ListingRequest, LocationStoreType } from '../../listing.types';
import { deriveQueryParams, scrollDoctorListingInView } from '../../utils';

export const ApplyFiltersButton = ({
  coids,
  program,
  siteType,
}: {
  coids: string;
  program: string;
  siteType: string;
}): JSX.Element => {
  const { t } = useI18n();
  const { isLocation, scSiteCookie, dotcms } = useOasApp();
  const isDotCMS = scSiteCookie === 'dotcms' || dotcms?.dotCmsHostId;

  const {
    setAppliedFilters,
    selectedGenders,
    selectedLanguages,
    showAcceptingNewPatientsOnly,
    teleMedicineFlg,
    selectedFacility,
    searchValue,
    keywordExact,
    locationUrlTitle,
    locationUrlTitleLabel,
    languages,
    zip: listingZip,
    setSearchValue,
  } = useListingContext();
  const { setLocation } = useDistanceContext();
  const { watch } = useFormContext();
  const router = useRouter();

  const locationFieldZipCodeValue = watch('zip');

  const inputSpecialtyId =
    searchValue?.type === AutocompleteQueries.specialtyId ? searchValue.value : undefined;
  const inputSpecialty =
    searchValue?.type === AutocompleteQueries.specialtyId ? searchValue.label : undefined;

  const disabled =
    !locationFieldZipCodeValue &&
    !listingZip &&
    selectedGenders?.length === 0 &&
    !showAcceptingNewPatientsOnly &&
    !teleMedicineFlg &&
    selectedLanguages?.length === 0 &&
    selectedFacility === ALL_FACILITY_OPTION.coid &&
    !searchValue?.value;

  const selectedLanguagesFromAvailable = useMemo(() => {
    if (
      !languages ||
      languages.length === 0 ||
      !selectedLanguages ||
      selectedLanguages.length === 0
    ) {
      return null;
    }

    return selectedLanguages.length === 1 && selectedLanguages[0] === 'English'
      ? null // Filter selectedLanguages to only those present from the available languages
      : intersection(selectedLanguages, languages).join(';') || null;
  }, [selectedLanguages, languages]);

  const selectedFacilityValuePair = selectedFacility?.split('|');
  const selectedFacilityCoid =
    selectedFacilityValuePair && selectedFacilityValuePair?.length > 0
      ? selectedFacilityValuePair[0]
      : undefined;
  const selectedFacilityLocationUrlTitle =
    selectedFacilityValuePair && selectedFacilityValuePair.length > 1
      ? selectedFacilityValuePair[1]
      : undefined;

  const updateQueryParams = useCallback(
    (params: Partial<ListingRequest>) => {
      const queryParams = deriveQueryParams({
        ...params,
      });

      const paramsString = queryParams.toString();

      const isLocationFallback = window.location.pathname.includes('/locations');
      if (!isDotCMS && (isLocation || isLocationFallback)) {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${paramsString && `?${paramsString}`}`
        );
        return;
      }

      const segments = window.location.pathname
        .split('/')
        // Remove empty segments and 'physicians' segment, point is to be dynamic for other main routes
        // and not hardcode a list of them
        .filter((s) => s && s !== Pages.Physicians);
      const locationIdx = segments.indexOf(RouteSegments.Locations);

      const preservedLocationPath =
        locationIdx !== -1 ? segments.slice(0, locationIdx + 2).join('/') : '';

      const newPath = isLocationFallback
        ? `/${preservedLocationPath}${LISTING_PAGE}`
        : LISTING_PAGE;

      router.push(
        {
          pathname: newPath,
          query: paramsString ? Object.fromEntries(queryParams) : null,
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [isLocation, router, isDotCMS]
  );

  const onApplyHandler = () => {
    let location: LocationStoreType = { address: '', zip: '', latitude: null, longitude: null };

    // if the location RHF field is empty
    // we clear the location in distance context and in session storage
    // todo: just fire an event from the location component when cleared
    // its a simple but a robust approach

    const rawLocationData = sessionStorage.getItem('selectedLocation') || '';
    const storedLocation = rawLocationData ? JSON.parse(rawLocationData) : null;
    if (storedLocation && storedLocation.zipCode === locationFieldZipCodeValue) {
      const { latitude, longitude, zipCode, address } = storedLocation;

      location = {
        address: (address as string) || '',
        zip: (zipCode as string) || '',
        latitude: parseFloat(latitude as string),
        longitude: parseFloat(longitude as string),
      };

      setLocation(location);
    } else {
      setLocation({ address: '', zip: '', latitude: null, longitude: null });
    }

    const hasFacilityDropdownSelected =
      selectedFacilityCoid && selectedFacilityCoid !== ALL_FACILITY_OPTION.coid;

    if (
      hasFacilityDropdownSelected &&
      locationUrlTitleLabel &&
      searchValue?.type === AutocompleteQueries.locationUrlTitle
    ) {
      setSearchValue(undefined);
    }

    // Set sortType to DISTANCE_SORT_BY only when location has valid coordinates, otherwise clear distance sorting.
    const sortType = location?.latitude && location?.longitude ? DISTANCE_SORT_BY : null;

    const urlQueryParams = {
      gender: selectedGenders.length > 1 ? null : selectedGenders?.[0] || null,
      languages: selectedLanguagesFromAvailable,
      showAcceptingNewPatientsOnly,
      teleMedicineFlg,
      facilityCoid:
        selectedFacilityCoid === ALL_FACILITY_OPTION.coid ? null : selectedFacilityCoid || null,
      zip: location?.zip || '',
      latitude: location?.latitude || null,
      longitude: location?.longitude || null,
      keywordExact,
      keywords: searchValue?.type === AutocompleteQueries.keywords ? searchValue.value : null,
      locationUrlTitle: selectedFacilityLocationUrlTitle || locationUrlTitle,
      locationUrlTitleLabel: hasFacilityDropdownSelected ? null : locationUrlTitleLabel,
      specialty: inputSpecialty || null,
      sortType,
    };

    updateQueryParams(urlQueryParams);

    setAppliedFilters({
      ...DEFAULT_REQ_PAYLOAD,
      coids,
      program,
      siteType,
      ...urlQueryParams,
      specialty: inputSpecialty || null,
      specialtyId: inputSpecialtyId || null,
      sortType,
    });

    scrollDoctorListingInView();
  };

  return (
    <NeuButton className="w-100" onNeuClick={onApplyHandler} disabled={disabled}>
      {t('OAS.Listing.Filter.Apply')}
    </NeuButton>
  );
};
